// components/NotificationComponent.js
import React from "react";
import { Bell, MessageSquare, AlertCircle, ClipboardList } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNotifications } from "app/contexts/NotificationContext";
import EmployeeProfile from "../common_component/Notifier";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDeleteLeft, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { getEthiopicDateTime } from "app/utils/custom_functions";
import { useTranslation } from "react-i18next";

const NotificationComponent = () => {
  const { t } = useTranslation();
  const { notifications, markAsRead } = useNotifications();

  const getNotificationIcon = (type) => {
    switch (type) {
      case "case_assignment":
        return <ClipboardList className="text-blue-500" />;
      case "new_message":
        return <MessageSquare className="text-green-500" />;
      case "system_alert":
        return <AlertCircle className="text-red-500" />;
      default:
        return <Bell className="text-gray-500" />;
    }
  };

  const getNotificationTypeLabel = (type) => {
    switch (type) {
      case "case_assignment":
        return t("Case Assignment");
      case "new_message":
        return t("New Message");
      case "system_alert":
        return t("System Alert");
      default:
        return t("Notification");
    }
  };

  return (
    <div className="w-full relative">
      {notifications.length === 0 ? (
        <div className="p-6 text-center text-gray-500">
          {t("No new notifications")}
        </div>
      ) : (
        <div
          className="flex flex-col gap-2 max-h-96 overflow-y-auto"
          style={{ zIndex: 20 }}
        >
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className="flex flex-col p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => markAsRead(notification.id)}
            >
              {/* Row 1: Icon, Employee/System, Delete */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xl">
                    {getNotificationIcon(notification.type)}
                  </span>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {!notification?.from_user ? (
                      t("System")
                    ) : (
                      <EmployeeProfile request={notification?.from_user} />
                    )}
                  </h3>
                </div>
                <button
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    markAsRead(notification.id);
                  }}
                  title={t("Delete notification")}
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </button>
              </div>

              {/* Message */}
              <p className="text-gray-600 text-sm mt-2 ml-2 line-clamp-2">
                {notification.message?.replace(/_/g, " ")}
              </p>

              {/* Row 3: Type and Date */}
              <div className="flex justify-between ml-2 items-center mt-2 text-xs text-gray-400">
                <span>
                  {getNotificationTypeLabel(notification.type)} •{" "}
                  {getEthiopicDateTime({
                    datetime: notification?.timestamp,
                    displayHour: true,
                  })}
                </span>
                <span>
                  {formatDistanceToNow(new Date(notification.timestamp))} {t("ago")}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationComponent;